import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Loader2, Wand2, Eye, X } from "lucide-react";
import { fetchGeoEvolution, fetchGeoMonthlyMatrix, deleteGeoRun, createGeoSyntheticRun, fetchGeoRun } from "@/lib/api";
import PromptResponsesBlock from "./PromptResponsesBlock";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const ENGINE_META = {
  chatgpt: { label: "ChatGPT", color: "#10A37F" },
  claude: { label: "Claude", color: "#C97A6A" },
  gemini: { label: "Gemini", color: "#4285F4" },
  perplexity: { label: "Perplexity", color: "#1FB6FF" },
};

export default function EvolutionPanel({ clientId }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["geo-evolution", clientId],
    queryFn: () => fetchGeoEvolution(clientId, 12),
  });
  const matrixQ = useQuery({
    queryKey: ["geo-monthly-matrix", clientId],
    queryFn: () => fetchGeoMonthlyMatrix(clientId, "2026-07"),
  });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [viewingRun, setViewingRun] = useState(null);
  const del = useMutation({
    mutationFn: (runId) => deleteGeoRun(clientId, runId),
    onSuccess: (r) => {
      toast.success(`Run borrado (${r.deleted_results} resultados)${r.was_snapshot ? " · era snapshot" : ""}`);
      qc.invalidateQueries({ queryKey: ["geo-evolution", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-monthly-matrix", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-runs", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-overview", clientId] });
      setPendingDelete(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo borrar el run"),
  });

  const synth = useMutation({
    mutationFn: (monthYm) => createGeoSyntheticRun(clientId, { month_ym: monthYm }),
    onSuccess: (r) => {
      toast.success(`Baseline creado: ${r.label} (${r.prompts_count} prompts × ${r.engines.length} engines)`);
      qc.invalidateQueries({ queryKey: ["geo-evolution", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-monthly-matrix", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-runs", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-overview", clientId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo crear el baseline"),
  });

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500">Cargando evolución…</div>;
  }
  const runs = q.data?.runs || [];
  const showCharts = runs.length >= 2;

  // Build the wide data shape Recharts expects.
  const wideRows = runs.map((r, i) => {
    const row = {
      idx: i,
      label: r.label,
      date: new Date(r.date).toLocaleDateString("es-AR", { day: "2-digit", month: "short" }),
      is_snapshot: r.is_snapshot,
    };
    for (const eng of Object.keys(ENGINE_META)) {
      const point = (q.data?.series?.[eng] || []).find((p) => p.run_id === r.id);
      row[eng] = point?.score ?? null;
    }
    return row;
  });

  // Build a wide row for category visibility (avg position)
  const positionRows = runs.map((r, i) => {
    const row = {
      idx: i,
      date: new Date(r.date).toLocaleDateString("es-AR", { day: "2-digit", month: "short" }),
    };
    for (const eng of Object.keys(ENGINE_META)) {
      const point = (q.data?.series?.[eng] || []).find((p) => p.run_id === r.id);
      row[`${eng}_pos`] = point?.avg_position ?? null;
    }
    return row;
  });

  return (
    <div className="space-y-6" data-testid="geo-evolution-panel">
      {/* MONTHLY MATRIX — prompts × month, results/mentions out of 4 */}
      <MonthlyMatrix data={matrixQ.data} isLoading={matrixQ.isLoading} />

      {!showCharts && runs.length < 2 && (
        <div className="rounded-md border border-dashed border-slate-300 p-6 text-center bg-slate-50/50" data-testid="geo-evolution-empty">
          <p className="text-sm text-slate-600">
            {runs.length === 0
              ? "Todavía no hay runs completadas."
              : "Se necesita otra run completada para mostrar los gráficos de evolución."}
          </p>
          <p className="text-xs text-slate-500 mt-2">
            La tabla mensual arriba se llena automáticamente a fin de cada mes. Podés correr análisis en el medio para checkpoints intermedios.
          </p>
        </div>
      )}

      {showCharts && (
        <>
          {/* SCORE EVOLUTION */}
          <section>
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
              Evolución del score de visibilidad
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Score combinado (categoría + reconocimiento de marca) por motor a través del tiempo. Día 0 = primera run completada.
            </p>
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={wideRows} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {Object.entries(ENGINE_META).map(([k, m]) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      name={m.label}
                      stroke={m.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* POSITION EVOLUTION */}
          <section>
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
              Evolución de la posición media en categorías
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Posición promedio en la que aparece la marca cuando es mencionada. Menor es mejor (1 = primera).
            </p>
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={positionRows} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <YAxis reversed allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {Object.entries(ENGINE_META).map(([k, m]) => (
                    <Line
                      key={`${k}_pos`}
                      type="monotone"
                      dataKey={`${k}_pos`}
                      name={m.label}
                      stroke={m.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}

      {/* RUNS TABLE with delete button */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-display text-lg font-semibold text-slate-900">
            Historial de runs
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const ym = window.prompt("¿Qué mes de baseline querés crear? (formato YYYY-MM, ej: 2026-07)", "2026-07");
              if (!ym) return;
              if (!/^\d{4}-\d{2}$/.test(ym)) {
                toast.error("Formato inválido. Usá YYYY-MM (ej: 2026-07)");
                return;
              }
              if (window.confirm(`Se creará un run SINTÉTICO de baseline para ${ym}\ncon cero menciones en todos los prompts × engines.\n\n¿Confirmás?`)) {
                synth.mutate(ym);
              }
            }}
            disabled={synth.isPending}
            className="gap-1.5 text-xs h-8 border-slate-300"
            data-testid="geo-create-synthetic-baseline"
            title="Crear un run sintético con cero menciones (para marcar un baseline sin llamar a los engines)"
          >
            {synth.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            Baseline sintético
          </Button>
        </div>
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Fecha</th>
                <th className="px-3 py-2 text-left font-medium">Label</th>
                <th className="px-3 py-2 text-center font-medium">Día 0</th>
                <th className="px-3 py-2 text-right font-medium w-24">Acción</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs italic">Sin runs todavía.</td></tr>
              ) : runs.slice().reverse().map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">
                    {new Date(r.date).toLocaleString("es-AR", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="px-3 py-2 text-slate-800">{r.label}</td>
                  <td className="px-3 py-2 text-center">
                    {r.is_snapshot ? (
                      <span className="text-emerald-700 text-xs font-medium">Snapshot</span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingRun(r.id)}
                        className="h-7 text-slate-500 hover:text-sky-600 hover:bg-sky-50 gap-1"
                        title="Ver los prompts y respuestas de este run"
                        data-testid={`geo-run-view-${r.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {pendingDelete === r.id ? (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => del.mutate(r.id)}
                            disabled={del.isPending}
                            className="text-xs px-2 py-1 bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"
                            data-testid={`geo-run-confirm-delete-${r.id}`}
                          >
                            {del.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirmar"}
                          </button>
                          <button
                            onClick={() => setPendingDelete(null)}
                            className="text-xs px-2 py-1 text-slate-500 hover:text-slate-800"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDelete(r.id)}
                          className="h-7 text-slate-400 hover:text-rose-600 hover:bg-rose-50 gap-1"
                          title={r.is_snapshot ? "Borrar este run (era snapshot, se limpia también)" : "Borrar este run"}
                          data-testid={`geo-run-delete-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {/* MODAL: Ver detalle de un run */}
      {viewingRun && (
        <RunDetailModal
          clientId={clientId}
          runId={viewingRun}
          onClose={() => setViewingRun(null)}
        />
      )}
    </div>
  );
}

// ─── Run Detail Modal ────────────────────────────────────────────────
function RunDetailModal({ clientId, runId, onClose }) {
  const q = useQuery({
    queryKey: ["geo-run", clientId, runId],
    queryFn: () => fetchGeoRun(clientId, runId),
    enabled: !!runId,
  });

  // Group results by prompt_id
  const byPrompt = {};
  const promptOrder = [];
  for (const r of q.data?.results || []) {
    if (!byPrompt[r.prompt_id]) {
      byPrompt[r.prompt_id] = {
        prompt: {
          text: r.prompt_text || "(sin texto)",
          prompt_type: r.prompt_type,
        },
        rows: [],
      };
      promptOrder.push(r.prompt_id);
    }
    byPrompt[r.prompt_id].rows.push(r);
  }
  const run = q.data?.run || {};

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center py-6 overflow-y-auto"
      onClick={onClose}
      data-testid="geo-run-detail-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-6 my-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 p-4 sticky top-0 bg-white rounded-t-lg z-10">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">
              {run.label || "Run"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {run.started_at && new Date(run.started_at).toLocaleString("es-AR", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
              {run.is_monthly_close && (
                <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">CIERRE DE MES</span>
              )}
              {run.is_synthetic && (
                <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">SINTÉTICO</span>
              )}
              {run.is_snapshot && (
                <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200">SNAPSHOT</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-800 p-1"
            data-testid="geo-run-detail-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {q.isLoading && (
            <div className="text-sm text-slate-500 py-8 text-center">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Cargando respuestas…
            </div>
          )}
          {q.error && (
            <div className="text-sm text-rose-600 py-4">
              No se pudo cargar el detalle del run.
            </div>
          )}
          {!q.isLoading && promptOrder.length === 0 && (
            <div className="text-sm text-slate-400 italic py-8 text-center">
              Este run no tiene resultados registrados.
            </div>
          )}
          {promptOrder.map((pid) => (
            <PromptResponsesBlock
              key={pid}
              prompt={byPrompt[pid].prompt}
              rows={byPrompt[pid].rows}
              defaultOpen={promptOrder.length <= 3}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
function MatrixCell({ v, total }) {
  if (!v) return <span className="text-slate-300">—</span>;
  const results = v.results ?? 0;
  const mentions = v.mentions ?? 0;
  const resPct = total > 0 ? results / total : 0;
  const menPct = total > 0 ? mentions / total : 0;
  const resTone = resPct >= 0.75 ? "text-emerald-700" : resPct >= 0.5 ? "text-sky-700" : resPct > 0 ? "text-slate-700" : "text-slate-300";
  const menTone = menPct >= 0.5 ? "text-emerald-700 font-semibold" : menPct >= 0.25 ? "text-amber-700 font-semibold" : menPct > 0 ? "text-slate-700 font-semibold" : "text-slate-300 font-semibold";
  return (
    <div className="text-center text-xs leading-tight">
      <span className={resTone}>{results}/{total}</span>
      <span className="text-slate-300 mx-1">·</span>
      <span className={menTone}>{mentions}/{total}</span>
    </div>
  );
}

function MonthlyMatrix({ data, isLoading }) {
  if (isLoading) {
    return <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500">Cargando tabla mensual…</div>;
  }
  if (!data || !data.prompts?.length) {
    return null;
  }
  const monthLabel = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const names = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${names[m - 1]} '${String(y).slice(-2)}`;
  };
  const total = data.total_engines || 4;

  const categoryPrompts = data.prompts.filter((p) => p.prompt_type === "category");
  const brandPrompts = data.prompts.filter((p) => p.prompt_type === "brand");

  return (
    <div className="space-y-6" data-testid="geo-monthly-matrix">
      {/* CATEGORY PROMPTS — real AI visibility metric */}
      {categoryPrompts.length > 0 && (
        <section data-testid="geo-monthly-matrix-category">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Evolución de menciones en categoría
            </h3>
            <span className="text-[10px] text-slate-500">
              Cada celda: <span className="text-slate-700 font-semibold">resultados/{total}</span> · <span className="text-emerald-700 font-semibold">menciones/{total}</span>
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Prompts de categoría (sin nombrar la marca). Baseline julio&nbsp;2026 en cero. Fin de cada mes muestra si la IA nos menciona organicamente en respuestas de descubrimiento.
          </p>
          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium sticky left-0 bg-slate-50 z-10">Prompt</th>
                  {data.months.map((m) => (
                    <th key={m.ym} className="px-3 py-2 text-center font-medium whitespace-nowrap">
                      {monthLabel(m.ym)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categoryPrompts.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                    <td className="px-3 py-2 sticky left-0 bg-white hover:bg-slate-50/40 max-w-[420px]">
                      <span className="text-xs text-slate-700 leading-snug">{p.text}</span>
                    </td>
                    {data.months.map((m) => (
                      <td key={m.ym} className="px-3 py-2 border-l border-slate-50">
                        <MatrixCell v={data.matrix[p.id]?.[m.ym]} total={total} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* BRAND PROMPTS — perception / narrative (mentions metric N/A) */}
      {brandPrompts.length > 0 && (
        <section data-testid="geo-monthly-matrix-brand">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Percepción de marca por la IA
            </h3>
            <span className="text-[10px] text-slate-500">
              Cada celda: <span className="text-slate-700 font-semibold">respuestas/{total}</span>
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Prompts que nombran la marca (le pasamos la URL, la mención está garantizada). Lo relevante acá es <strong>cómo</strong> nos describe la IA y si esa narrativa cambia mes a mes. Por ahora solo trackeamos si el motor respondió — el análisis de percepción va a llegar en el próximo tramo.
          </p>
          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium sticky left-0 bg-slate-50 z-10">Prompt</th>
                  {data.months.map((m) => (
                    <th key={m.ym} className="px-3 py-2 text-center font-medium whitespace-nowrap">
                      {monthLabel(m.ym)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {brandPrompts.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                    <td className="px-3 py-2 sticky left-0 bg-white hover:bg-slate-50/40 max-w-[420px]">
                      <span className="text-xs text-slate-700 leading-snug">{p.text}</span>
                    </td>
                    {data.months.map((m) => {
                      const v = data.matrix[p.id]?.[m.ym];
                      const r = v?.results ?? 0;
                      const tone = r >= 3 ? "text-emerald-700" : r > 0 ? "text-slate-700" : "text-slate-300";
                      return (
                        <td key={m.ym} className="px-3 py-2 border-l border-slate-50">
                          <div className={`text-center text-xs font-medium ${tone}`}>{r}/{total}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
